import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Logo } from "@/components/Logo";
import { PhoneCall, Hash, RotateCcw, Voicemail, ShieldCheck, MessageSquare } from "lucide-react";
import { SMS_CONSENT_TEXT } from "@/pages/GetQuote";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const recordedRows = (brand) => [
  ["Company / Brand", `the registered SMS sender that delivered the consent message (${brand})`],
  ["Caller number", "the caller's mobile number (E.164)"],
  ["Number called", "the RoofLead number the caller dialed"],
  ["Timestamp", "date and time of the key press (UTC)"],
  ["Consent action", "keypad press of 1 (method ivr_keypad_press_1); declines and no-response outcomes are also logged"],
  ["Script version", "the version identifier of the consent script that was played"],
  ["Exact script", "the full verbatim consent script the caller heard"],
  ["Call ID", "the Twilio Call SID of the call that captured the key press"],
  ["Scope", "follow-up texts for the RoofLead platform development & testing program (no roofing services)"],
];

const TONES = {
  orange: "bg-orange-500/10 border-orange-500/30 text-orange-400",
  emerald: "bg-emerald-500/10 border-emerald-500/30 text-emerald-400",
  rose: "bg-rose-500/10 border-rose-500/30 text-rose-400",
};

function Step({ icon: Icon, title, children, testid, tone = "orange" }) {
  return (
    <li className="flex gap-4" data-testid={testid}>
      <div className={`shrink-0 w-10 h-10 rounded-full border flex items-center justify-center ${TONES[tone]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-white font-semibold">{title}</p>
        <div className="text-slate-400 text-sm mt-1">{children}</div>
      </div>
    </li>
  );
}

function Quote({ children, testid }) {
  return (
    <blockquote data-testid={testid}
      className="bg-slate-900 border-l-4 border-orange-500 rounded-r-lg p-4 font-mono text-sm text-slate-100 leading-relaxed">
      {children || "Loading…"}
    </blockquote>
  );
}

export default function SmsConsent() {
  const [c, setC] = useState(null);
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/compliance`).then((r) => setC(r.data)).catch(() => {});
  }, []);
  const brand = c?.brand || "Brandon Rinker Jr.";

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100">
      <nav className="border-b border-slate-800 bg-[#0B0F17]/85 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 h-16 flex items-center justify-between">
          <Logo />
          <Link to="/" className="text-sm text-slate-400 hover:text-white" data-testid="sms-consent-nav-home">← Back to home</Link>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-16" data-testid="sms-consent-page">
        <p className="font-mono text-xs uppercase tracking-widest text-orange-400 mb-3">SMS Consent</p>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mb-3">
          {brand} SMS Opt-In Process (IVR)
        </h1>
        <p className="text-slate-500 text-sm mb-10" data-testid="sms-consent-updated">
          Last updated: June 2026 · Brand: <span className="text-slate-300">{brand}</span> · Consent script version:{" "}
          <span className="font-mono text-orange-400" data-testid="sms-consent-script-version">{c?.script_version || "…"}</span>
        </p>

        <div className="space-y-10 text-slate-300 leading-relaxed">
          <section className="bg-slate-900 border border-slate-800 rounded-xl p-6" data-testid="sms-consent-summary">
            <h2 className="font-display text-2xl font-bold text-white mb-3">Summary</h2>
            <p className="mb-3">
              <span className="text-white font-semibold">RoofLead platform development &amp; testing program.</span>{" "}
              RoofLead is a software platform in development and testing. RoofLead is not a roofing contractor and does not
              provide roofing services, inspections, estimates, or roofing work.
            </p>
            <p className="mb-3" data-testid="sms-consent-sender">
              <span className="text-white font-semibold">SMS sender:</span> all text messages in this program are sent by{" "}
              <span className="text-white font-semibold">{brand}</span>, who is developing and testing the RoofLead software.
            </p>
            <p>
              When a caller dials the program number and the call is not answered, {brand} answers with an automated voice
              (IVR) message <span className="text-white font-semibold">before any text message is sent</span>. The caller
              must press <span className="font-mono text-orange-400">1</span> to agree to receive service-related texts.
              {" "}{brand} never sends a text asking someone to opt in. If the caller does not press 1, no text message is
              sent and the caller can leave a voicemail for a callback.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white mb-4">The flow, step by step</h2>
            <ol className="space-y-5">
              <Step icon={PhoneCall} title="1. Caller dials and the call is missed" testid="sms-consent-step-1">
                The caller places the call voluntarily. {brand} never cold-calls or auto-dials consumers.
              </Step>
              <Step icon={MessageSquare} title="2. The caller hears the consent message" testid="sms-consent-step-2">
                The full consent script (below) is played, including the program description, message frequency,
                rates disclosure, STOP/HELP instructions, and the Privacy Policy and Terms locations.
              </Step>
              <Step icon={Hash} title="3. Pressing 1 records consent" testid="sms-consent-step-3" tone="emerald">
                The caller hears: <span className="font-mono text-slate-200">"{c?.ivr_accept}"</span> The consent record is
                saved first; only then is the first text message sent.
              </Step>
              <Step icon={RotateCcw} title="4. No response: the message is repeated once" testid="sms-consent-step-4">
                The caller hears <span className="font-mono text-slate-200">"{c?.ivr_retry}"</span> and the full consent
                script is played one more time.
              </Step>
              <Step icon={Voicemail} title="5. Decline or still no response: voicemail, no SMS" testid="sms-consent-step-5" tone="rose">
                Pressing 2 (or any key other than 1), or not responding after the second prompt, means{" "}
                <span className="text-white font-semibold">no text message is ever sent</span>. The caller hears:{" "}
                <span className="font-mono text-slate-200">"{c?.ivr_decline_voicemail}"</span> The voicemail is saved
                for a callback.
              </Step>
            </ol>
          </section>

          <section data-testid="sms-consent-web-optin">
            <h2 className="font-display text-2xl font-bold text-white mb-3">Web form opt-in (alternative)</h2>
            <p className="text-slate-400 text-sm mb-3">
              Visitors can also opt in on <Link to="/get-quote" className="text-orange-400 underline" data-testid="sms-consent-get-quote-link">/get-quote</Link>{" "}
              by checking an SMS checkbox that is <span className="text-white font-semibold">unchecked by default</span> and
              optional. Submitting the form without checking it sends no texts. The exact checkbox text (stored with each opt-in,
              together with timestamp, form URL, IP address and user agent) is:
            </p>
            <Quote testid="sms-consent-web-checkbox-text">{`"${SMS_CONSENT_TEXT} See our Privacy Policy and Terms of Service."`}</Quote>
          </section>

          <section id="disclosure">
            <h2 className="font-display text-2xl font-bold text-white mb-3">Exact IVR consent script</h2>
            <p className="text-slate-400 text-sm mb-3">Verbatim text played to the caller (version {c?.script_version}).</p>
            <Quote testid="sms-consent-ivr-script">{c?.ivr_script && `"${c.ivr_script}"`}</Quote>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white mb-3">Exact messages</h2>
            <div className="space-y-4">
              <div><p className="text-sm text-white font-semibold mb-2">First text after pressing 1</p><Quote testid="sms-consent-first-sms">{c?.first_sms}</Quote></div>
              <div><p className="text-sm text-white font-semibold mb-2">Reply to HELP</p><Quote testid="sms-consent-help-sms">{c?.help_sms}</Quote></div>
              <div><p className="text-sm text-white font-semibold mb-2">Reply to STOP (also STOPALL, UNSUBSCRIBE, CANCEL, END, QUIT)</p><Quote testid="sms-consent-stop-sms">{c?.stop_sms}</Quote></div>
              <div><p className="text-sm text-white font-semibold mb-2">Reply to START (also UNSTOP, RESUME)</p><Quote testid="sms-consent-start-sms">{c?.start_sms}</Quote></div>
            </div>
            <p className="text-slate-400 text-xs mt-3">
              After the first text, replies may be assisted by AI and always identify {brand}. STOP immediately revokes
              consent; no further automatic texts are sent and a future missed call replays the consent message.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-emerald-400" /> What is recorded for every consent event
            </h2>
            <ul className="list-disc pl-6 space-y-1" data-testid="sms-consent-record-list">
              {recordedRows(brand).map(([k, v]) => (
                <li key={k}><span className="text-white font-semibold">{k}</span> — {v}</li>
              ))}
            </ul>
            <p className="mt-3 text-slate-400 text-sm">
              Records are append-only and kept as proof of consent under CTIA and A2P 10DLC requirements. Consent applies
              only to the Brand and company the caller called; it is never shared with or reused by any other company.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white mb-3">Program details</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li><span className="text-white font-semibold">Program:</span> RoofLead platform development &amp; testing program — texts following up on the caller's call. No marketing texts. No roofing services are offered under the RoofLead brand.</li>
              <li><span className="text-white font-semibold">Message frequency:</span> varies.</li>
              <li><span className="text-white font-semibold">Cost:</span> message and data rates may apply.</li>
              <li><span className="text-white font-semibold">Opt-out:</span> reply STOP at any time. <span className="text-white font-semibold">Help:</span> reply HELP or call <span className="font-mono" data-testid="sms-consent-help-phone">{c?.help_phone}</span>.</li>
              <li><span className="text-white font-semibold">Consent is not a condition</span> of any purchase or service.</li>
              <li>{brand} will not sell or share mobile numbers or SMS consent with third parties for marketing.</li>
              <li>Carriers are not liable for delayed or undelivered messages.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white mb-3">Policies</h2>
            <ul className="space-y-1 font-mono text-sm" data-testid="sms-consent-urls">
              <li>Privacy Policy: <a href={c?.urls?.privacy} className="text-orange-400 underline" data-testid="sms-consent-privacy-link">{c?.urls?.privacy}</a></li>
              <li>Terms of Service: <a href={c?.urls?.terms} className="text-orange-400 underline" data-testid="sms-consent-terms-link">{c?.urls?.terms}</a></li>
              <li>This page: <a href={c?.urls?.sms_consent} className="text-orange-400 underline" data-testid="sms-consent-self-link">{c?.urls?.sms_consent}</a></li>
            </ul>
          </section>
        </div>
      </article>
    </div>
  );
}
